import styled from '@emotion/styled'

import { spacing } from '../../theme'

type SpacingKeys = keyof typeof spacing
type PaddingValue = SpacingKeys | SpacingKeys[]

type PadBoxProps = {
  /**
   * This can be a single value like 's' or an array like ['s', 'm', 'l', 'xl']
   */
  padding: PaddingValue
}

/**
 * `PadBox` Component
 *
 * @overview
 * The `PadBox` component is a layout utility designed to apply uniform padding
 * based on predefined spacing constants from the design system.
 *
 * @props
 *
 * @prop {PaddingValue} padding - Determines the padding applied to the component.
 *                                Can be a single spacing constant key (e.g., 'small')
 *                                or an array of keys to fine-tune padding according to
 *                                CSS shorthand conventions.
 *
 * @example
 *
 * ```javascript
 * <PadBox padding="s">
 *   Content with small padding
 * </PadBox>
 * ```
 *
 * ```javascript
 * <PadBox padding={['s', 'l']}>
 *   Content with small padding on top and bottom, and large on right and left
 * </PadBox>
 * ```
 *
 * @notes
 * - If no padding prop is provided, it defaults to 'default' / 'm' (1 rem).
 * - The array for padding prop can follow CSS shorthand:
 *   [top & bottom, right & left],
 *   [top, right & left, bottom],
 *   or [top, right, bottom, left].
 */
const resolvePadding = (padding: PaddingValue): string => {
  if (typeof padding === 'string') {
    return spacing[padding]
  }

  return padding.map(val => spacing[val]).join(' ')
}

const PadBox = styled.div<PadBoxProps>`
  padding: ${props => resolvePadding(props.padding || 'default')};
`

export default PadBox
